/**
 * Utility for detecting programmatic vs user-initiated scrolling
 *
 * This module provides a way to distinguish between scrolls triggered by JavaScript
 * (like scrollIntoView, scrollTo, etc.) and scrolls initiated by user interaction
 * (like mouse wheel, touch gestures, keyboard navigation).
 */
type ScrollDetectorCallback = (isProgrammatic: boolean | undefined) => void

class ProgrammaticScrollDetector {
  private isProgrammaticScroll = false
  private callbacks: Set<ScrollDetectorCallback> = new Set()
  private isInitialized = false
  private TIMEOUT_DURATION = 2000

  constructor() {
    this.initializeGlobalFunctions()
  }

  private initializeGlobalFunctions() {
    if (typeof window === 'undefined' || this.isInitialized)
      return // Expose function to set programmatic scroll flag
    ;(window as unknown as Record<string, unknown>).setProgrammaticScroll = (
      value: boolean,
    ) => {
      this.isProgrammaticScroll = value
    }

    // Expose global flag for immediate checking - only if it doesn't exist
    if (
      !(
        (window as unknown as Record<string, unknown>)
          .__isProgrammaticScroll !== undefined
      )
    ) {
      Object.defineProperty(window, '__isProgrammaticScroll', {
        get: () => this.isProgrammaticScroll,
        set: (value: boolean) => {
          this.isProgrammaticScroll = value
        },
        configurable: true, // Allow redefinition
      })
    }

    this.isInitialized = true
  }

  /**
   * Register a callback to be notified of scroll events
   */
  addScrollListener(callback: ScrollDetectorCallback) {
    this.callbacks.add(callback)

    if (this.callbacks.size === 1) {
      // First callback, start listening
      this.startListening()
    }
  }

  /**
   * Remove a scroll callback
   */
  removeScrollListener(callback: ScrollDetectorCallback) {
    this.callbacks.delete(callback)

    if (this.callbacks.size === 0) {
      // No more callbacks, stop listening
      this.stopListening()
    }
  }

  private handleScroll = () => {
    const windowGlobal = window as unknown as Record<string, unknown>
    const globalProgrammaticFlag = windowGlobal.__isProgrammaticScroll

    const isProgrammatic =
      this.isProgrammaticScroll || globalProgrammaticFlag ? true : undefined

    // Notify all callbacks
    this.callbacks.forEach((callback) => callback(isProgrammatic))

    // Only reset flags if they were actually set to true
    if (this.isProgrammaticScroll) {
      setTimeout(() => {
        this.isProgrammaticScroll = false
      }, this.TIMEOUT_DURATION)
    }

    if (globalProgrammaticFlag) {
      setTimeout(() => {
        windowGlobal.__isProgrammaticScroll = false // Set to false instead of deleting
      }, this.TIMEOUT_DURATION)
    }
  }

  private startListening() {
    if (typeof document !== 'undefined') {
      document.addEventListener('scroll', this.handleScroll)
    }
  }

  private stopListening() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('scroll', this.handleScroll)
    }
  }

  /**
   * Clean up all listeners and global functions
   */
  destroy() {
    this.stopListening()
    this.callbacks.clear()

    this.isProgrammaticScroll = false

    if (typeof window !== 'undefined') {
      delete (window as unknown as Record<string, unknown>)
        .setProgrammaticScroll
      delete (window as unknown as Record<string, unknown>)
        .__isProgrammaticScroll
    }

    this.isInitialized = false
  }
}

// Create singleton instance
const scrollDetector = new ProgrammaticScrollDetector()

/**
 * Hook-like function to listen for scroll events with programmatic detection
 */
export const useProgrammaticScrollDetector = (
  callback: (isProgrammatic: boolean | undefined) => void,
) => {
  const addListener = () => scrollDetector.addScrollListener(callback)
  const removeListener = () => scrollDetector.removeScrollListener(callback)

  return { addListener, removeListener }
}

/**
 * Mark the next scroll as programmatic
 */
export const markScrollAsProgrammatic = () => {
  if (typeof window !== 'undefined') {
    const windowWithGlobals = window as unknown as Record<string, unknown>
    const setProgrammaticScroll = windowWithGlobals.setProgrammaticScroll as
      | ((value: boolean) => void)
      | undefined

    // Set both flags immediately to ensure detection
    setProgrammaticScroll?.(true)
    windowWithGlobals.__isProgrammaticScroll = true

    // Also trigger the internal flag for redundancy
    scrollDetector['isProgrammaticScroll'] = true
  }
}

export default scrollDetector
